import {BrowserModule} from '@angular/platform-browser';
import {DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule,} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {OidcAuth, ProleisWebAppModule, translateLoaderFactory,} from '@proleis/proleis-web-app';
import {HomeViewComponent} from './home.view.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ProleisRestClientModule} from '@proleis/proleis-rest-client';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {OverlayModule} from '@angular/cdk/overlay';
import {SuppliersModule} from './modules/suppliers.module';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AuthModule, OpenIdConfiguration, StsConfigHttpLoader, StsConfigLoader,} from 'angular-auth-oidc-client';
import {HttpClient} from '@angular/common/http';
import {ScannerComponent} from './modules/scanner/scanner.component';
import {ScannerModule} from '@proleis/proleis-web-scanner';
import {ServiceWorkerModule} from '@angular/service-worker';
import {environment} from '../environments/environment';
import {ScannerSheetComponent} from './modules/scanner/scanner.sheet.component';
import {APP_GUIDS, appGuids} from './app.guids';
import {AppConfig, OidcLoginConfig,} from '@proleis/proleis-web-app/lib/models/app.config';
import {OidcAuthMethod} from './oidc.auth.method';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {map} from 'rxjs/operators';
import {SafePipe} from './safe.pipe';
import {MatButtonModule} from '@angular/material/button';
import {MatListModule} from '@angular/material/list';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatCardModule} from '@angular/material/card';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {SuppliersErrorHandler} from './suppliers.error.handler';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

export function getConfigLocation() {
  const hostname = window.location.hostname;
  return `./assets/config/${ hostname }.config.json`;
}

export function loadAuthConfig(http: HttpClient) {
  const config$ = http.get<AppConfig>(getConfigLocation()).pipe(
    map((appConfig) => {
      const oidcConfig: OidcLoginConfig =
        appConfig.loginConfig.loginTypes.filter(
          (loginType) => loginType.loginType === 'OIDC',
        )[0];
      const openIdConfig: OpenIdConfiguration = {
        clientId: oidcConfig.clientId,
        authWellknownEndpointUrl: oidcConfig.authWellknownEndpoint,
        authority: oidcConfig.stsServer,
        responseType: oidcConfig.responseType,
        scope: oidcConfig.scope,
        postLogoutRedirectUri: oidcConfig.postLogoutRedirectUrl,
        redirectUrl: oidcConfig.redirectUrl,
        autoUserInfo: oidcConfig.autoUserInfo,
      };
      return openIdConfig;
    }),
  );
  return new StsConfigHttpLoader(config$);
}

@NgModule({
  declarations: [
    AppComponent,
    HomeViewComponent,
    ScannerComponent,
    ScannerSheetComponent,
  ],
  imports: [
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: loadAuthConfig,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ProleisWebAppModule.forRoot({
      configUrl: getConfigLocation(),
    }),
    ProleisRestClientModule.forRoot({
      configUrl: getConfigLocation(),
    }),
    MatButtonModule,
    MatListModule,
    MatIconModule,
    MatTooltipModule,
    OverlayModule,
    SuppliersModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    ScannerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  bootstrap: [AppComponent],
  providers: [
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    {
      provide: APP_GUIDS,
      useValue: appGuids,
    },
    {
      provide: OidcAuth,
      useClass: OidcAuthMethod,
    },
    SafePipe,
    {
      provide: ErrorHandler,
      useClass: SuppliersErrorHandler,
    },
  ],
})
export class AppModule {}
